import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, Minus, Save } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { useTitle } from '../hooks/useTitle';

interface ToolFormData {
  name: string;
  description: string;
  category: string;
  pricing_type: string;
  pricing_details: string;
  features: string[];
  use_cases: string[];
  pros: string[];
  cons: string[];
  website_url: string;
  image_url: string;
}

const initialFormData: ToolFormData = {
  name: '',
  description: '',
  category: '',
  pricing_type: '',
  pricing_details: '',
  features: [''],
  use_cases: [''],
  pros: [''],
  cons: [''],
  website_url: '',
  image_url: ''
};

export function ToolForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { setTitle } = useTitle();
  const [formData, setFormData] = useState<ToolFormData>(initialFormData);
  const [loading, setLoading] = useState(false);
  const isEditing = !!id;

  useEffect(() => {
    setTitle(isEditing ? 'Modifier un outil' : 'Nouvel outil');
    if (isEditing) {
      fetchTool();
    }
  }, [isEditing, setTitle]);

  const fetchTool = async () => {
    try {
      const { data, error } = await supabase
        .from('ai_tools')
        .select('*')
        .eq('id', id)
        .single();

      if (error) throw error;
      setFormData(data);
    } catch (error) {
      console.error('Error fetching tool:', error);
      toast.error('Erreur lors du chargement de l\'outil');
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleArrayChange = (index: number, value: string, field: keyof ToolFormData) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].map((item, i) => i === index ? value : item)
    }));
  };

  const handleAddItem = (field: keyof ToolFormData) => {
    setFormData(prev => ({
      ...prev,
      [field]: [...prev[field], '']
    }));
  };

  const handleRemoveItem = (index: number, field: keyof ToolFormData) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { error } = isEditing
        ? await supabase
            .from('ai_tools')
            .update(formData)
            .eq('id', id)
        : await supabase
            .from('ai_tools')
            .insert([formData]);

      if (error) throw error;

      toast.success(isEditing ? 'Outil mis à jour avec succès' : 'Outil créé avec succès');
      navigate('/admin');
    } catch (error) {
      console.error('Error saving tool:', error);
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <button
              onClick={() => navigate('/admin')}
              className="text-gray-600 hover:text-gray-900 flex items-center gap-2"
            >
              <ArrowLeft className="w-5 h-5" />
              Retour
            </button>
            <h1 className="text-2xl font-bold">
              {isEditing ? 'Modifier un outil' : 'Nouvel outil'}
            </h1>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-lg p-6">
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Nom de l'outil
                  </label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Catégorie
                  </label>
                  <input
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  required
                  rows={4}
                  className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                />
              </div>

              {/* Pricing */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type de tarification
                  </label>
                  <select
                    name="pricing_type"
                    value={formData.pricing_type}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  >
                    <option value="">Sélectionner...</option>
                    <option value="Gratuit">Gratuit</option>
                    <option value="Freemium">Freemium</option>
                    <option value="Payant">Payant</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Détails de tarification
                  </label>
                  <input
                    type="text"
                    name="pricing_details"
                    value={formData.pricing_details}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* URLs */}
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL du site web
                  </label>
                  <input
                    type="url"
                    name="website_url"
                    value={formData.website_url}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    URL de l'image
                  </label>
                  <input
                    type="url"
                    name="image_url"
                    value={formData.image_url}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                  />
                </div>
              </div>

              {/* Features */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Fonctionnalités
                  </label>
                  <button
                    type="button"
                    onClick={() => handleAddItem('features')}
                    className="text-orange-500 hover:text-orange-600"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={feature}
                        onChange={(e) => handleArrayChange(index, e.target.value, 'features')}
                        className="flex-1 px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index, 'features')}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Minus className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Use Cases */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Cas d'utilisation
                  </label>
                  <button
                    type="button"
                    onClick={() => handleAddItem('use_cases')}
                    className="text-orange-500 hover:text-orange-600"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
                <div className="space-y-2">
                  {formData.use_cases.map((useCase, index) => (
                    <div key={index} className="flex gap-2">
                      <input
                        type="text"
                        value={useCase}
                        onChange={(e) => handleArrayChange(index, e.target.value, 'use_cases')}
                        className="flex-1 px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveItem(index, 'use_cases')}
                        className="text-red-500 hover:text-red-600"
                      >
                        <Minus className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Pros & Cons */}
              <div className="grid md:grid-cols-2 gap-6">
                {/* Pros */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Points forts
                    </label>
                    <button
                      type="button"
                      onClick={() => handleAddItem('pros')}
                      className="text-orange-500 hover:text-orange-600"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.pros.map((pro, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={pro}
                          onChange={(e) => handleArrayChange(index, e.target.value, 'pros')}
                          className="flex-1 px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index, 'pros')}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Minus className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Cons */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Points d'attention
                    </label>
                    <button
                      type="button"
                      onClick={() => handleAddItem('cons')}
                      className="text-orange-500 hover:text-orange-600"
                    >
                      <Plus className="w-5 h-5" />
                    </button>
                  </div>
                  <div className="space-y-2">
                    {formData.cons.map((con, index) => (
                      <div key={index} className="flex gap-2">
                        <input
                          type="text"
                          value={con}
                          onChange={(e) => handleArrayChange(index, e.target.value, 'cons')}
                          className="flex-1 px-4 py-2 rounded-lg border focus:ring-2 focus:ring-orange-500 focus:outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(index, 'cons')}
                          className="text-red-500 hover:text-red-600"
                        >
                          <Minus className="w-5 h-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading}
                  className={`bg-orange-500 text-white px-6 py-2 rounded-lg flex items-center gap-2
                    ${loading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-orange-600'}`}
                >
                  <Save className="w-5 h-5" />
                  {loading ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}